import { cloneDeep } from 'lodash';

type entityKeys = keyof EntitiesState;

export class EntityRootSelector {
  state: RootState;
  channel: ChannelSelector;
  preferenceProduct: PreferenceProductSelector;
  shopItemCategory: ShopItemCategorySelector;
  shopItem: ShopItemSelector;
  dataItem: DataItemSelector;
  dataItemViewFavourite: DataItemSelector;
  shopItemFavourite: ShopItemSelector;
  userFavouriteTag: UserFavouriteTagSelector;

  constructor(state: RootState) {
    this.state = state;
    this.channel = new ChannelSelector(this);
    this.preferenceProduct = new PreferenceProductSelector(this);
    this.shopItemCategory = new ShopItemCategorySelector(this);
    this.shopItem = new ShopItemSelector(this);
    this.dataItem = new DataItemSelector(this);
    this.userFavouriteTag = new UserFavouriteTagSelector(this);

    this.dataItemViewFavourite = new DataItemSelector(
      this,
      state.favourite.data_item_ids
    );
    this.shopItemFavourite = new ShopItemSelector(
      this,
      state.favourite.shop_item_ids
    );
  }
}

export const getEntitySelector = (state: RootState) =>
  new EntityRootSelector(state);

abstract class EntitySelector<Entity, LabelledEntity extends Entity> {
  key: entityKeys;
  selector: EntityRootSelector;
  private _searchResults?: number[];

  constructor(selector: EntityRootSelector, searchResults?: number[]) {
    this.selector = selector;
    this._searchResults = searchResults;
  }
  abstract setParents(e: LabelledEntity): void;

  get searchResults(): number[] {
    return (
      this._searchResults ||
      this.selector.state.entities[this.key].searchResults
    );
  }

  assignAttributes(e: Entity): LabelledEntity {
    return e as LabelledEntity;
  }

  _getById(id: number): Entity | undefined {
    return this.selector.state.entities[this.key].byId[String(id)];
  }

  getById(id: number): LabelledEntity | undefined {
    const e = this._getById(id);
    return e && this.assignAttributes(e);
  }

  getFullById(id: number): LabelledEntity | undefined {
    let e = this.getById(id);
    if (e) {
      e = cloneDeep(e);
      this.setParents(e);
    }
    return e;
  }

  getByIdOrEmpty(id: number): LabelledEntity {
    // defensive programming
    // even if invalid data, still try to return entity
    return this.getById(id) || ({} as LabelledEntity);
  }

  getByIds(ids: number[], full: boolean = false): LabelledEntity[] {
    return ids
      .map(id => (full ? this.getFullById(id) : this.getById(id)))
      .filter(e => !!e) as LabelledEntity[];
  }

  getFullByIds(ids: number[], full: boolean = false): LabelledEntity[] {
    return this.getByIds(ids, true);
  }

  all(full: boolean = false): LabelledEntity[] {
    return Object.keys(this.selector.state.entities[this.key].byId)
      .map(key => Number(key))
      .map(id => (full ? this.getFullById(id) : this.getById(id)))
      .filter(e => !!e) as LabelledEntity[];
  }

  allBySearchResults(full: boolean = false): LabelledEntity[] {
    const ids = this.searchResults;
    if (ids) {
      return full ? this.getFullByIds(ids) : this.getByIds(ids);
    } else {
      // some entities don't have searchResults keys for now ...
      return this.all(full);
    }
  }

  allFullBySearchResults(): LabelledEntity[] {
    return this.allBySearchResults(true);
  }
}

class ChannelSelector extends EntitySelector<Channel, Channel> {
  key: entityKeys = 'channel';
  setParents(e: Channel) {} // DO NOTHING
}

class PreferenceProductSelector extends EntitySelector<
  PreferenceProduct,
  PreferenceProduct
> {
  key: entityKeys = 'preference_product';
  setParents(e: PreferenceProduct) {} // DO NOTHING
}

class ShopItemCategorySelector extends EntitySelector<
  ShopItemCategory,
  ShopItemCategory
> {
  key: entityKeys = 'shop_item_category';
  setParents(e: ShopItemCategory) {
    e.channel = this.selector.channel.getByIdOrEmpty(e.company_channel_id);
  }
}

class ShopItemSelector extends EntitySelector<ShopItem, ShopItem> {
  key: entityKeys = 'shop_item';
  setParents(e: ShopItem) {
    e.channel = this.selector.channel.getByIdOrEmpty(e.company_channel_id);
  }
}

class DataItemSelector extends EntitySelector<DataItem, DataItem> {
  key: entityKeys = 'data_item';
  setParents(e: DataItem) {
    e.channel = this.selector.channel.getByIdOrEmpty(e.company_channel_id);
    e.preference_product = this.selector.preferenceProduct.getByIdOrEmpty(
      e.preference_product_id
    );
  }
}

class UserFavouriteTagSelector extends EntitySelector<
  UserFavouriteTag,
  UserFavouriteTag
> {
  key: entityKeys = 'user_favourite_tag';
  setParents(e: UserFavouriteTag) {} // DO NOTHING
  getSuperTags() {
    return this.all().filter(t => !t.parent_id);
  }
}



// WEBPACK FOOTER //
// ./app/selectors/index.ts